// A second, selectable built-in rig: Quaternius's "Universal Animation
// Library 2" (UAL2) Standard mannequin and its Mannequin F variant, sharing
// the same UE-style skeleton and proportions (see assets/ATTRIBUTION-ual2.md
// for provenance). Mannequin F ships with no baked clips of its own by
// design (its README says to retarget the Standard file's animations onto
// it), so both are driven the same way here: the canonical rig's 45 clips,
// retargeted onto whichever mesh's own skeleton via ual2Retarget.ts's
// bone-mapping/retarget pipeline. The result is a PrototypeGltf exactly like
// loadPrototypeGltf's - same clip names, same PROTOTYPE_SKELETON bone names
// (ual2Retarget renames the resolved bones) - so it is a drop-in
// createPrototypeInstance() source: no rig-specific code needed anywhere
// downstream (AnimationGalleryStage, attachToHand, team recolour, etc).
//
// Kept deliberately identical (module body only - asset URLs are necessarily
// local to each consumer) to its sibling copy at
// sunder/sstk-sunder-web/src/prototype/src/ual2.ts.
import { loadPrototypeGltf, type PrototypeGltf } from './loader';
import { buildUal2PrototypeGltf } from '../rig/ual2Retarget';

// Built from a variable, not `new URL('literal', import.meta.url)` directly:
// see clips.ts's PROTOTYPE_GLB_URL for why (Vite library mode otherwise
// inlines these multi-MB rigs as base64 straight into dist/index.js).
const ual2StandardGlbAssetPath = './assets/ual2-standard.glb';
const ual2MannequinFGlbAssetPath = './assets/ual2-mannequin-f.glb';
export const UAL2_STANDARD_GLB_URL = new URL(ual2StandardGlbAssetPath, import.meta.url).href;
export const UAL2_MANNEQUIN_F_GLB_URL = new URL(ual2MannequinFGlbAssetPath, import.meta.url).href;

const cache = new Map<string, Promise<PrototypeGltf>>();

/**
 * Load a UAL2 GLB (Standard or Mannequin F) and retarget the canonical rig's
 * 45 clips onto it. Memoised per URL, mirroring loadPrototypeGltf's own
 * cache-once behaviour; a transient failure clears the cache entry so the
 * next caller gets a fresh attempt rather than a permanently memoised
 * rejection.
 */
export function loadUal2PrototypeGltf(url: string): Promise<PrototypeGltf> {
  let entry = cache.get(url);
  if (!entry) {
    entry = (async () => {
      const { GLTFLoader } = await import('three/examples/jsm/loaders/GLTFLoader.js');
      const [canonical, ual2] = await Promise.all([loadPrototypeGltf(), new GLTFLoader().loadAsync(url)]);
      const built = buildUal2PrototypeGltf(canonical, { scene: ual2.scene });
      return { scene: built.scene, animations: built.animations };
    })().catch((err: unknown) => {
      cache.delete(url);
      throw err;
    });
    cache.set(url, entry);
  }
  return entry;
}

/** UAL2 Standard (male-proportioned) mannequin, retargeted onto the shared 45-clip set. */
export function loadUal2StandardPrototypeGltf(): Promise<PrototypeGltf> {
  return loadUal2PrototypeGltf(UAL2_STANDARD_GLB_URL);
}

/** UAL2 Mannequin F (female-proportioned) mannequin, retargeted onto the shared 45-clip set. */
export function loadUal2MannequinFPrototypeGltf(): Promise<PrototypeGltf> {
  return loadUal2PrototypeGltf(UAL2_MANNEQUIN_F_GLB_URL);
}
